import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarIcon } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import GooglePlacesAutocomplete from "@/components/GooglePlacesAutocomplete";
import { weddingService } from "@/services/wedding.service";

export const EditWeddingDialog = ({ open, onOpenChange, wedding, onUpdate }) => {
  const [formData, setFormData] = useState({
    wedding_date: undefined,
    venue: null
  });
  const [calendarOpen, setCalendarOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open && wedding) {
      setFormData({
        wedding_date: wedding.wedding_date ? new Date(wedding.wedding_date) : undefined,
        venue: wedding.venue ? {
          name: wedding.venue,
          address: wedding.venue_location?.address || wedding.venue,
          location: wedding.venue_location?.location || null,
          placeId: wedding.venue_location?.place_id || null
        } : null
      });
    }
  }, [open, wedding]);

  const handleVenueChange = (place) => {
    setFormData({ ...formData, venue: place });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const updateData = {
        wedding_date: formData.wedding_date ? format(formData.wedding_date, "yyyy-MM-dd") : wedding.wedding_date,
        venue: formData.venue?.name || wedding.venue,
        venue_location: formData.venue ? {
          name: formData.venue.name,
          address: formData.venue.address,
          location: formData.venue.location,
          place_id: formData.venue.placeId
        } : wedding.venue_location
      };

      console.log('Updating wedding with data:', updateData);
      const result = await weddingService.updateWedding(wedding.id, updateData);
      console.log('Update result:', result);
      
      toast.success("Wedding details updated successfully!");
      onUpdate && onUpdate();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update wedding:", error);
      toast.error(error.message || "Failed to update wedding details");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-w-[95vw]">
        <DialogHeader>
          <DialogTitle>Edit Wedding Details</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 overflow-hidden">
          <div className="space-y-2">
            <Label>Wedding Date</Label>
            <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !formData.wedding_date && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4 flex-shrink-0" />
                  <span className="truncate">
                    {formData.wedding_date ? format(formData.wedding_date, "MM/dd/yyyy") : "Pick a date"}
                  </span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start" side="bottom">
                <Calendar
                  mode="single"
                  selected={formData.wedding_date}
                  defaultMonth={formData.wedding_date}
                  onSelect={(date) => {
                    setFormData({ ...formData, wedding_date: date });
                    setCalendarOpen(false);
                  }}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="space-y-2">
            <Label>Venue</Label>
            <div className="w-full min-w-0">
              <GooglePlacesAutocomplete
                value={formData.venue}
                onChange={handleVenueChange}
                placeholder="Search for wedding venue..."
                types={['establishment']}
                className="w-full"
              />
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} className="w-full sm:w-auto">
              {submitting ? "Updating..." : "Update Wedding"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditWeddingDialog;
